import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { HelpAskAnswer, HelpAskOutcome } from '../api';
import './HelpAsk.css';

/** A minute on screen, unanswered, and the row leaves on its own. */
const EXPIRE_MS = 60_000;
/** The one beat of acknowledgement after an answer, then gone. */
const THANKS_MS = 1500;
/** The strip's close (matches the CSS transition). */
const LEAVE_MS = 220;

const OPTIONS: ReadonlyArray<{ answer: HelpAskAnswer; label: string }> = [
  { answer: 'yes', label: 'Yes' },
  { answer: 'sort_of', label: 'Sort of' },
  { answer: 'not_really', label: 'Not really' },
];

/**
 * "Was this any help?" (2026-09-15 拍板, §4.5) — the field's own top while
 * it is up (2026-09-21: one shape with the composer, not a row floating over
 * it), rising once an Ember's arrival has cleared: the question, three
 * answers in the words a person would actually say, and a ×. It asks about
 * the thing that just happened (this), not about Ember or the product, so it
 * stays in the conversation's own register instead of turning into a rating.
 *
 * It leaves on its own. The person starting to type (`draftStarted`) or a
 * minute on screen ends it as `expired` — the clock runs only while the row
 * is in view (`paused` while an Ember page is open over the thread). The ×
 * is `dismissed`; an answer says Thank you for a beat, then goes. Every end
 * is reported once through `onClose` the moment it happens; `onDone` fires
 * after the fade so the parent can unmount. Leaving the page reports
 * nothing: a row never closed already says "left" on the server.
 */
export function HelpAsk({ paused, draftStarted, onClose, onDone }: {
  paused: boolean;
  draftStarted: boolean;
  onClose: (outcome: HelpAskOutcome, answer?: HelpAskAnswer) => void;
  onDone: () => void;
}): ReactElement {
  const [phase, setPhase] = useState<'open' | 'thanks' | 'leaving'>('open');
  const closed = useRef(false);
  const close = useRef(onClose);
  close.current = onClose;
  const done = useRef(onDone);
  done.current = onDone;

  const end = (outcome: HelpAskOutcome, answer?: HelpAskAnswer): void => {
    if (closed.current) return;
    closed.current = true;
    close.current(outcome, answer);
    setPhase(outcome === 'answered' ? 'thanks' : 'leaving');
  };

  useEffect(() => {
    if (phase !== 'open' || paused) return undefined;
    const t = window.setTimeout(() => end('expired'), EXPIRE_MS);
    return () => window.clearTimeout(t);
  }, [phase, paused]);

  useEffect(() => {
    if (draftStarted) end('expired');
  }, [draftStarted]);

  useEffect(() => {
    if (phase === 'open') return undefined;
    const t = window.setTimeout(
      () => (phase === 'thanks' ? setPhase('leaving') : done.current()),
      phase === 'thanks' ? THANKS_MS : LEAVE_MS,
    );
    return () => window.clearTimeout(t);
  }, [phase]);

  return (
    <div className={`convo-help is-${phase}`} role="group" aria-label="Was this any help?">
      <div className="convo-help-row">
        {phase === 'thanks' ? (
          <span className="convo-help-q">Thank you.</span>
        ) : (
          <>
            <span className="convo-help-q">Was this any help?</span>
            <span className="convo-help-opts">
              {OPTIONS.map((o) => (
                <button key={o.answer} type="button" className="convo-help-opt" onClick={() => end('answered', o.answer)}>
                  {o.label}
                </button>
              ))}
            </span>
            <button type="button" className="convo-help-x" aria-label="Close" onClick={() => end('dismissed')}>
              <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" /></svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
